import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
} from 'react-router-dom';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleNotch,
  faPhoneAlt,
  faSignOutAlt,
  faUser,
  faAngleDown,
  faTimes,
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import ReactGA from 'react-ga';
import { useCookies } from 'react-cookie';
import { defineCustomElements } from '@taf-ui/core/dist/loader';

import NonAuthorized, { NonAuthorizedMode } from './NonAuthorized/NonAuthorized';
import ValidationWrapper from './ValidationWrapper/ValidationWrapper';
import AdminAuthWrapper from './AdminAuthWrapper/AdminAuthWrapper';
import CookieNotification from './CookieNotification/CookieNotification';
import HealthDeclarationRoute from 'HealthDeclarationRoute/HealthDeclarationRoute';
import { KeycloakInstanceWrapper } from './Keycloak/Keycloak';

import 'App.css';

library.add(
  faCircleNotch,
  faPhoneAlt,
  faSignOutAlt,
  faUser,
  faAngleDown,
  faTimes,
  faCheckCircle,
  faTimesCircle,
);

export const RouterChildren = () => {
  return (
    <Routes>
      <Route
        path="/loggedout"
        element={<NonAuthorized mode={NonAuthorizedMode.KEYCLOAK_LOGOUT} />}
      />
      <Route
        path="/admin"
        element={<AdminAuthWrapper />}
      />
      <Route
        path="/:healthDeclarationId/*"
        element={
          <HealthDeclarationRoute render={healthDeclarationId => (
            <ValidationWrapper healthDeclarationId={healthDeclarationId} />
          )} />
        }
      />
      <Route
        path="/"
        element={<NonAuthorized mode={NonAuthorizedMode.ID_MISSED} />}
      />
    </Routes>
  );
};

void defineCustomElements();

const App = () => {
  const [cookies, setCookie] = useCookies(['allowCookie']);
  const [showCookiesModal, setShowCookiesModal] = useState(true);

  const [showCookiesModalDelay, setShowCookiesModalDelay] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const googleAnalyticsId = window.env?.GOOGLE_ANALYTICS_ID;
    if (googleAnalyticsId && cookies.allowCookie && JSON.parse(cookies.allowCookie as string)) {
      setShowCookiesModal(false);
      ReactGA.initialize(googleAnalyticsId);
      ReactGA.pageview(window.location.pathname + window.location.search);
    } else if (cookies.allowCookie && !JSON.parse(cookies.allowCookie as string )) {
      setShowCookiesModal(false);
    // Only for test build, to have the same behavior with cookie notification as we have on prod
    } else if (!googleAnalyticsId &&
      cookies.allowCookie && JSON.parse(cookies.allowCookie as string)) {
      setShowCookiesModal(false);
    }
  }, [cookies]);

  useEffect(() => {
    const timer = setTimeout(() => setShowCookiesModalDelay(true), 2000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const fetchEnv = async () => {
      const res = await fetch('/appenv');
      const brand = res.headers.get('X-Brand') || '';
      const country = res.headers.get('X-Country') || '';
      const locales = res.headers.get('X-Locales') || '';

      localStorage.setItem('brand', brand);
      localStorage.setItem('country', country);
      localStorage.setItem('locales', locales);

      KeycloakInstanceWrapper.getInstance();

      setIsLoading(false);
    };

    fetchEnv().catch(e => console.log(e));
  }, []);

  const handleCookieNotification = (data: boolean): void => {
    setCookie('allowCookie', data);
    setShowCookiesModal(false);
  };

  if (isLoading) {
    return null;
  }

  return (
    <BrowserRouter>
      {showCookiesModal && showCookiesModalDelay && <CookieNotification clickHandler={handleCookieNotification} />}
      <RouterChildren />
    </BrowserRouter>
  );
};

export default App;
